import { checkClaimsWeb, IOptionsWithTypes, spLogTags } from '@eventr/common'
import {
    ColoursObj,
    Icon,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    useDialogStore,
} from '@eventr/common-storybook'
import { IconButton } from '@mui/material'
import { useEffect, useState } from 'react'

import { useShallow } from 'zustand/react/shallow'
import {
    createChannel,
    useRocketChatContext,
} from '../../../feat/rocketchat/rocketChatProvider'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { RocketChannel } from '../../../state/rocketchat/rocketchat.interface'
import { liveToken } from '../../../state/rocketchatState/rocketchatSignals'
import { enableChat, Utils } from '../../../utils'
import { useIteratorProvider } from '../../state/itieratorProvider'
import { useSectionV2Context } from '../../state/sectionProvider'
import { useObjectContext } from '../providers/objectProvider'

interface IChatButtonProps {
    names: string[]
    _id: string
    editID?: string
    users: IOptionsWithTypes[]
    depth?: number
    addUserConfirmationText?: string
    addUserConfirmationHeader?: string
}

export const ChatButton = ({
    names,
    users,
    depth = 1,
    _id,
    editID,
    addUserConfirmationText,
    addUserConfirmationHeader,
}: IChatButtonProps) => {
    const { sectionData, isPublic } = useSectionV2Context()
    const { visibility } = useObjectContext()
    const { id } = useIteratorProvider()
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const {
        orgConfig,
        override,
        wpUser,
        isUserPartOfSelection,
        selectedItinID,
        rocketchannels,
    } = useBoundStore(
        useShallow((s) => {
            return {
                orgConfig: s.orgConfig,
                override: s.override,
                wpUser: s.wpUser,
                isUserPartOfSelection: s.isUserPartOfSelection,
                selectedItinID: s.selectedItinID,
                rocketchannels: s.rocketchannels[s.selectedItinID],
            }
        })
    )

    const { openChat, userRooms, isActivated } = useRocketChatContext()

    const [channel, setChannel] = useState<RocketChannel | undefined>()
    const isUserPartOf = isUserPartOfSelection(users)
    const [buttonState, setButtonState] = useState<
        'empty' | 'read' | 'unread' | 'loading' | undefined
    >()

    useEffect(() => {
        const chan = rocketchannels?.find((c) => {
            return c.object_id === _id
        })

        setChannel(chan)
    }, [rocketchannels, _id])

    useEffect(() => {
        if (channel && userRooms) {
            const room = userRooms.find((room) => {
                return room._id === channel.channel_id
            })
            if (room) {
                if ((room.unread ?? 0) > 0) {
                    setButtonState('unread')
                } else {
                    setButtonState('read')
                }
            } else {
                setButtonState(undefined)
            }
        } else {
            if (userRooms) {
                setButtonState(undefined)
            } else {
                setButtonState('loading')
            }
        }
    }, [userRooms, channel, setButtonState])

    if (!enableChat()) {
        return null
    }

    const createOrNavigateToChannel = () => {
        if (channel && channel?.members?.includes(wpUser?.id!)) {
            openChat(channel?.channel_id, 'c' + channel.section_id)
        } else {
            loadingSignal.value = true

            if (names.length !== 0) {
                const u = users.filter((u) => u.type === 'User')
                createChannel({
                    channel_name_parts: names,
                    itinerary_id: selectedItinID,
                    section_id: sectionData?.sectionID ?? 0, // Default to 0 if undefined
                    user_ids: u.map((u) => u?.option as number),
                    _id: _id,
                })
                    .then((c) => {
                        if (c.status === 'success') {
                            if (c.data.channel_id)
                                openChat(
                                    c.data.channel_id,
                                    'c' + (sectionData?.sectionID ?? 0)
                                )
                            bs().pullRocketChatChannels()
                        } else if (c.status === 'error') {
                            spLogTags('Error creating channel', [
                                'rocketchat',
                                'ERROR',
                            ])
                            spLogTags(c, ['rocketchat', 'ERROR'])
                        } else {
                            spLogTags(c, ['rocketchat', 'ERROR'])
                        }
                    })
                    .finally(() => {
                        loadingSignal.value = false
                    })
            }
        }
    }

    const handleAddUserClick = () => {
        let sectionID = sectionData?.sectionID
        replaceDialog({
            node: (
                <MobileConfirmDialog
                    text={
                        addUserConfirmationText ??
                        i18n.t(
                            'edit-itinerary.joinButtonConfirmationText.' +
                                sectionID
                        )
                    }
                    header={
                        addUserConfirmationHeader ??
                        i18n.t('edit-itinerary.joinButtonText.' + sectionID)
                    }
                    confirmBtnTextColour={ColoursObj.core.secondary}
                    cancelBtnTextColour={ColoursObj.states.error}
                    onConfirm={handleDialogConfirm}
                    onCancel={() => {
                        clearDialog()
                    }}
                    cancelText={i18n.t('info-dialog.no')}
                    confirmText={i18n.t('info-dialog.yes')}
                />
            ),
            backdropClosable: false,
        })
    }

    const handleDialogConfirm = () => {
        clearDialog()
        const sectionID = sectionData?.sectionID
        if (sectionID === 8) {
            window.pushPath(`/edit/activities/general/${_id}`)
            return
        }
        if (depth === 2) {
            window.pushPath(
                '/edit/Section/' +
                    sectionID?.toString() +
                    '/' +
                    id +
                    '/2/' +
                    _id
            )
        } else {
            if (sectionID) {
                window.pushPath(
                    '/edit/Section/' +
                        sectionID.toString() +
                        '/' +
                        (editID ?? _id)
                )
            }
        }
    }

    return (
        <>
            {!isActivated || isUserPartOf === undefined ? (
                <div />
            ) : isUserPartOf ? (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation()
                        Utils.hapticFeedback('light')
                        if (buttonState !== 'loading') {
                            if (liveToken.value !== undefined) {
                                createOrNavigateToChannel()
                            } else {
                                window.pushPath('/tabs/rocketchat')
                            }
                        }
                    }}
                >
                    {buttonState === 'unread' ? (
                        <Icon name="Chat_Notf" />
                    ) : (
                        buttonState !== 'loading' && (
                            <Icon
                                name={'Chat'}
                                iconColour={
                                    buttonState === 'read'
                                        ? ColoursObj.core.primary
                                        : ColoursObj.shades.shade2
                                }
                            />
                        )
                    )}
                </IconButton>
            ) : override === null ? (
                !orgConfig?.editInAppDisabled &&
                !isPublic &&
                visibility !== 'Public' &&
                checkClaimsWeb(['/'], ['editItinerary']) && (
                    <MainButton
                        size="small"
                        text={i18n.t('general.join')} //"+ Join"
                        icon="Add"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleAddUserClick()
                        }}
                    />
                )
            ) : (
                <div />
            )}
        </>
    )
}
